import React from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import * as Yup from 'yup';

import LabeledField from '@zr/components/Fields/LabeledField';
import SkillField from '@zr/components/Fields/SkillField';
import TagList from '@zr/jobseeker-public/apps/alerts/components/TagList';

import FormActions from '../../../components/FormActions';
import QuestionLabel from '../../../components/QuestionLabel';

import IndexedLegend from './IndexedLegend';
import FormButtons from './FormButtons';

import { patchProfileForm } from './utilities';

const SKILLS_INPUT = 'skills_input';
const SKILLS = 'skills';

export function Skills({
  index, onContinue, onError, onSkip, profile, skillLimit, submitButtonText, totalScreeningQuestions,
  isInJs6650Test, setIsLoading,
}) {
  const handleContinue = async (values, formik) => {
    // `setIsLoading(true)` so that we can disable the close button while the
    // screen is processing/loading
    setIsLoading(true);
    await patchProfileForm({
      // according to @dnakolan and @vu, we set this to differentiate from a backfill script that used the profile api
      // @dnakolan says it doesn't hurt to always send this flag in
      skills_confirmed: 1,
      [SKILLS]: values[SKILLS],
    }, formik, onContinue, onError);
  };

  return (
    <Formik
      // on the off chance they added skills getting to this form...
      initialValues={{ [SKILLS_INPUT]: '', [SKILLS]: profile.skills ?? [] }}
      onSubmit={handleContinue}
      validationSchema={Yup.object().shape({
        [SKILLS]: Yup.array().min(3, 'Please add at least 3 skills'),
      })}
    >
      {({
        errors, handleChange, handleSubmit, isSubmitting, touched, values, setFieldValue, setValues,
      }) => {
        const selectedSkills = values[SKILLS];

        const handleSkillsSelect = (skills) => {
          if (skills.length <= skillLimit) {
            setValues({
              [SKILLS_INPUT]: '',
              [SKILLS]: skills,
            });
            return true;
          }
          return false;
        };

        const handleSkillsChange = (e) => {
          handleChange(e);
        };

        const handleSkillsDelete = (deletedSkillName) => () => {
          setFieldValue(SKILLS, selectedSkills.filter(
            (selectedSkill) => (selectedSkill.name !== deletedSkillName),
          ));
        };

        return (
          <form className="Suggestion" onSubmit={handleSubmit}>
            <fieldset>
              {isInJs6650Test && (
                <QuestionLabel
                  as="legend"
                  html="Please provide three or more skills that make you a good fit for this position."
                  number={index}
                  totalQuestions={totalScreeningQuestions}
                />
              )}
              {!isInJs6650Test && (
                <IndexedLegend index={index} text="Please provide three or more skills that make you a good fit for this position." />
              )}
              <LabeledField
                as={SkillField}
                input={{
                  disabled: isSubmitting || selectedSkills.length >= skillLimit,
                  id: `LabeledField_${SKILLS_INPUT}`,
                  name: SKILLS_INPUT,
                  onChange: handleSkillsChange,
                  type: 'text',
                  value: values[SKILLS_INPUT],
                }}
                label="Add Skills"
                meta={{
                  error: errors[SKILLS],
                  touched: touched[SKILLS_INPUT],
                }}
                placeholder="Add Skills"
                onSelect={handleSkillsSelect}
                skills={selectedSkills}
              />
              <TagList
                items={selectedSkills}
                itemToString={(skill) => (skill.name)}
                onDelete={handleSkillsDelete}
                renderItem={(skill) => (skill.name)}
                type="skill"
              />
              {isInJs6650Test && (
                <FormActions
                  disableContinue={isSubmitting}
                  disableSkip={isSubmitting}
                  onSkip={onSkip}
                  submitButtonText={submitButtonText}
                />
              )}
              {!isInJs6650Test && (
                <FormButtons disabled={isSubmitting} onSkip={onSkip} submitButtonText={submitButtonText} />
              )}
            </fieldset>
          </form>
        );
      }}
    </Formik>
  );
}

Skills.propTypes = {
  index: PropTypes.number.isRequired,
  isInJs6650Test: PropTypes.bool,
  onContinue: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  onSkip: PropTypes.func,
  profile: PropTypes.shape({
    skills: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  skillLimit: PropTypes.number.isRequired,
  setIsLoading: PropTypes.func,
  submitButtonText: PropTypes.string,
  totalScreeningQuestions: PropTypes.number.isRequired,
};

Skills.defaultProps = {
  isInJs6650Test: false,
  onSkip: null,
  profile: {
    skills: [],
  },
  setIsLoading: () => {},
  submitButtonText: 'Continue',
};

export default Skills;
